// Mesaj aksiyonları (yanıtla dışındaki üst fonksiyonlar) — TEK kaynak.
//
// Neden hook: bu mantık yalnız ChatPage'de vardı; sağ alttaki yüzen düğmeyle açılan
// ChatDock panelinde hiç bağlanmamıştı → hover araç çubuğundaki düğmeler (İlet, Ata,
// Kaydet, Düzenle, Sil, Kopyala…) TIKLANIYOR AMA HİÇBİR ŞEY OLMUYORDU (18.08.2026
// kullanıcı bildirimi). İki yerde ayrı ayrı yazmak yerine ortak hook: biri düzelince
// ikisi de düzelir, ayrışamazlar.
//
// Döner: { onMessageAction, onDelete, openTaskPicker, portals }
//   portals → modal/popover'ları basan JSX; çağıran bileşenin köküne konur.
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast, confirm } from '../../stores/uiStore'
import { chatService } from '../../services/chatService'
import AssignPopover from './AssignPopover'
import ForwardModal from './ForwardModal'
import ListPickerPopover from '../tasks/detail/ListPickerPopover'

// copyLinkUrl: "Bağlantıyı kopyala" için özel URL üreteci. Varsayılan kanal
// deeplink'idir; belge görüşmesinde belgenin KENDİ sayfası paylaşılmalı.
export function useChatMessageActions({ company, channelId, refreshMsgs, refreshConv, onAi, copyLinkUrl }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null)        // {id, body}
  const [forwardMsg, setForwardMsg] = useState(null)
  const [assignPick, setAssignPick] = useState(null)  // {message, x, y}
  const [taskPick, setTaskPick] = useState(null)      // {messageId, x, y}
  const err = (e) => toast.error('Hata', e.response?.data?.detail || e.message)
  // Popover'lar tıklanan düğmenin yanında açılır; koordinat yoksa ekran ortası (yedek)
  const at = (ev) => ({ x: ev?.clientX ?? Math.round(window.innerWidth / 2), y: ev?.clientY ?? 200 })

  const openTaskPicker = (messageId, ev) => setTaskPick({ messageId, ...at(ev) })

  const onMessageAction = async (m, action, ev) => {
    if (action === 'copytext') { navigator.clipboard?.writeText(m.body || ''); toast.success('Metin kopyalandı') }
    else if (action === 'copylink') {
      const url = copyLinkUrl?.(m) || `${window.location.origin}/chat/c/${channelId}?m=${m.id}`
      navigator.clipboard?.writeText(url); toast.success('Bağlantı kopyalandı')
    }
    else if (action === 'unread') { try { await chatService.markUnread(company, channelId, m.id); refreshConv?.(); toast.success('Okunmadı işaretlendi') } catch (e) { err(e) } }
    else if (action === 'edit') setEditing({ id: m.id, body: m.body || '' })
    else if (action === 'save' || action === 'unsave') {
      // Liste ÖNBELLEĞİ geçersiz kılınmalı: kaydettikten sonra "Kaydedilenler"
      // eski önbellekten çizilirse kullanıcı mesajı orada göremiyor ve kaydetme
      // çalışmamış sanıyor. refreshMsgs → yer imi düğmesi dolu/boş güncellensin.
      const on = action === 'save'
      try {
        await chatService.saveMessage(company, m.id, on)
        qc.invalidateQueries({ queryKey: ['chat-saved'] })
        refreshMsgs?.()
        toast.success(on ? 'Kaydedildi' : 'Kayıttan çıkarıldı',
                      on ? 'Kaydedilenler listesinde' : undefined)
      } catch (e) { err(e) }
    }
    else if (action === 'task') openTaskPicker(m.id, ev)
    else if (action === 'ai') onAi?.()
    else if (action === 'translate') {
      try {
        const r = await chatService.translateMessage(company, m.id)
        const d = r?.data || {}
        // Çeviri saklanmaz; toast ile gösterilir (okuyucuya özel)
        if (d.translation) toast.info('Çeviri', d.translation)
        else toast.warning('Çevrilemedi', d.message || 'AI yanıt vermedi')
      } catch (e) { err(e) }
    }
    else if (action === 'pin' || action === 'unpin') {
      try {
        await chatService.pinMessage(company, m.id, { unpin: action === 'unpin' })
        qc.invalidateQueries({ queryKey: ['chat-pins', channelId] })
        refreshMsgs?.()
        toast.success(action === 'pin' ? 'Sabitlendi' : 'Sabitleme kaldırıldı')
      } catch (e) { err(e) }   // sunucu sınırı (en fazla 3) sessizce yutulmaz
    }
    else if (action === 'forward') setForwardMsg(m)
    else if (action === 'assign') setAssignPick({ message: m, ...at(ev) })
    else if (action === 'resolve' || action === 'unresolve') {
      try {
        await chatService.resolveMessage(company, m.id, action === 'resolve')
        refreshMsgs?.(); qc.invalidateQueries({ queryKey: ['chat-assigned'] })
        toast.success(action === 'resolve' ? 'Çözüldü işaretlendi' : 'Çözüm geri alındı')
      } catch (e) { err(e) }
    }
    else toast.info('Yakında', 'Bu işlem sonraki sürümde')
  }

  // Düzenleme ARTIK ayrı popup değil — composer'a düşer (inline). Bu yüzden hook
  // yalnız durumu tutar; kaydet/iptal'i çağıran (ChatPage/ChatDock) composer'a bağlar.
  const saveEdit = async (id, text) => {
    try { await chatService.editMessage(company, id, text); setEditing(null); refreshMsgs?.(); toast.success('Güncellendi') }
    catch (e) { err(e) }
  }
  const cancelEdit = () => setEditing(null)

  // Kanal/sekme değişince açık modal/popover'lar kapansın — başka kanalın mesajını
  // düzenler hâlde kalmasın.
  const closeAll = () => { setEditing(null); setForwardMsg(null); setAssignPick(null); setTaskPick(null) }

  const onDelete = async (m) => {
    const ok = await confirm({ title: 'Mesaj silinsin mi?', variant: 'danger', confirmLabel: 'Sil' })
    if (!ok) return
    try { await chatService.deleteMessage(company, m.id); refreshMsgs?.() } catch (e) { err(e) }
  }

  const portals = (
    <>
      {forwardMsg && (
        <ForwardModal company={company} message={forwardMsg} onClose={() => setForwardMsg(null)}
          onSent={() => refreshConv?.()} />
      )}
      {assignPick && (
        <AssignPopover company={company} x={assignPick.x} y={assignPick.y} current={assignPick.message.assigned_to}
          onClose={() => setAssignPick(null)}
          onPick={async (uid) => {
            try {
              await chatService.assignMessage(company, assignPick.message.id, uid)
              refreshMsgs?.(); qc.invalidateQueries({ queryKey: ['chat-assigned'] })
              toast.success(uid ? 'Mesaj atandı' : 'Atama kaldırıldı')
            } catch (e) { err(e) }
            setAssignPick(null)
          }} />
      )}
      {taskPick && (
        <ListPickerPopover company={company} x={taskPick.x} y={taskPick.y} onClose={() => setTaskPick(null)}
          onPick={async (listId) => {
            const mid = taskPick.messageId
            setTaskPick(null)
            try {
              const r = await chatService.createTaskFromMessage(company, mid, listId)
              refreshMsgs?.()
              toast.success('Görev oluşturuldu', `#${r?.data?.task_id}`)
            } catch (e) { err(e) }
          }} />
      )}
    </>
  )

  return { onMessageAction, onDelete, openTaskPicker, closeAll, portals, editing, saveEdit, cancelEdit }
}
